import { ethers } from "ethers";
import type { BaseDerivationTypedData } from "./baseDerivation";

/** Only locally authored messages may be displayed for derivation failures. */
export class DogeDerivationError extends Error {}

function storageKey(typed: BaseDerivationTypedData): string {
  const context = ethers.TypedDataEncoder.hash(
    typed.domain,
    typed.types,
    typed.message,
  );
  return `stealthos-doge-key-fingerprint-v1:${context}`;
}

export function verifyDogeDerivationSignature(
  typed: BaseDerivationTypedData,
  signature: string,
): string {
  try {
    const bytes = ethers.getBytes(signature);
    if (
      ![64, 65].includes(bytes.length) ||
      ethers
        .verifyTypedData(typed.domain, typed.types, typed.message, signature)
        .toLowerCase() !== String(typed.message.wallet).toLowerCase()
    )
      throw new Error();
    // Preserve the exact signing bytes: changing serialization changes the key.
    return ethers.hexlify(bytes);
  } catch {
    throw new DogeDerivationError(
      "The privacy signature does not match this wallet. Reconnect before continuing.",
    );
  }
}

function fingerprint(
  typed: BaseDerivationTypedData,
  signature: string,
): string {
  // SHA-256(signature) is the spending seed, so it must never be persisted as
  // a fingerprint. Use a separate, domain-separated one-way digest instead.
  return ethers.keccak256(
    ethers.concat([
      ethers.toUtf8Bytes("StealthOS DogeOS key fingerprint v1"),
      verifyDogeDerivationSignature(typed, signature),
    ]),
  );
}

function readFingerprint(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    throw new DogeDerivationError(
      "The privacy-key check could not read browser storage. Enable storage and reconnect.",
    );
  }
}

export function assertDogeDerivationFingerprint(
  typed: BaseDerivationTypedData,
  signature: string,
): void {
  const current = fingerprint(typed, signature);
  const stored = readFingerprint(storageKey(typed));
  if (stored !== null && stored !== current)
    throw new DogeDerivationError(
      "This wallet's privacy signature has changed. Restore with your StealthOS recovery phrase, or reconnect using the original wallet software before receiving or spending funds.",
    );
}

/** Remember only a non-secret digest after connection/account checks pass. */
export function rememberDogeDerivationFingerprint(
  typed: BaseDerivationTypedData,
  signature: string,
): void {
  assertDogeDerivationFingerprint(typed, signature);
  const key = storageKey(typed);
  const value = fingerprint(typed, signature);
  try {
    localStorage.setItem(key, value);
    if (localStorage.getItem(key) !== value) throw new Error();
  } catch {
    throw new DogeDerivationError(
      "The privacy-key check could not be saved. Enable browser storage and reconnect.",
    );
  }
}
